import { useState } from 'react';
import { Button } from '@7shifts/sous-chef-base';
import { useTranslation } from '@src/i18n';
import backspaceKey from '@src/assets/icons/backspace-key.svg';
import './PinEntryGrid.css';

const KEY_ROWS = [
    ['1', '2', '3'],
    ['4', '5', '6'],
    ['7', '8', '9']
] as const;

interface PinEntryGridProps {
    onSubmit: (pin: string) => void;
    disabled: boolean;
    loading?: boolean;
    submitLabel?: string;
}

export function PinEntryGrid({
    onSubmit,
    disabled,
    loading = false,
    submitLabel
}: PinEntryGridProps) {
    const [pin, setPin] = useState('');
    const { t } = useTranslation();

    function pressDigit(digit: string) {
        setPin(prev => prev + digit);
    }

    function pressClear() {
        setPin('');
    }

    function pressBackspace() {
        setPin(prev => prev.slice(0, -1));
    }

    function pressSubmit() {
        if (!pin.length) return;
        const submitted = pin;
        setPin('');
        onSubmit(submitted);
    }

    const pinStatus =
        pin.length === 0
            ? t('pinGrid.srNoDigits')
            : pin.length === 1
            ? t('pinGrid.srDigitCountSingular')
            : t('pinGrid.srDigitCountPlural', { count: pin.length });

    return (
        <div className="pin-entry-grid">
            <h3 className="pin-entry-grid__title" id="pin-entry-title">
                {t('pinGrid.title')}
            </h3>
            {/* Screen reader status — announced on every pin change */}
            <div aria-live="polite" className="pin-entry-grid__sr-status">
                {pinStatus}
            </div>
            <div className="pin-entry-grid__card">
                {/* aria-hidden: state is conveyed via the live region above */}
                <div
                    className="pin-entry-grid__field"
                    aria-hidden="true"
                    data-testid="pin-display"
                >
                    {Array.from({ length: pin.length }, (_, i) => (
                        <span
                            key={i}
                            className="pin-entry-grid__dot pin-entry-grid__dot--filled"
                        />
                    ))}
                </div>
                <div
                    className="pin-entry-grid__keys"
                    role="group"
                    aria-labelledby="pin-entry-title"
                >
                    {KEY_ROWS.flat().map(digit => (
                        <button
                            key={digit}
                            type="button"
                            className="pin-entry-grid__key"
                            disabled={disabled}
                            onClick={() => pressDigit(digit)}
                            data-testid={`pin-key-${digit}`}
                        >
                            {digit}
                        </button>
                    ))}
                    <button
                        type="button"
                        className="pin-entry-grid__key pin-entry-grid__key--label"
                        disabled={disabled || pin.length === 0}
                        onClick={pressClear}
                        data-testid="pin-clear"
                    >
                        {t('pinGrid.clear')}
                    </button>
                    <button
                        type="button"
                        className="pin-entry-grid__key"
                        disabled={disabled}
                        onClick={() => pressDigit('0')}
                        data-testid="pin-key-0"
                    >
                        0
                    </button>
                    <button
                        type="button"
                        className="pin-entry-grid__key pin-entry-grid__key--backspace"
                        disabled={disabled || pin.length === 0}
                        onClick={pressBackspace}
                        data-testid="pin-backspace"
                        aria-label={t('pinGrid.backspace')}
                    >
                        <img
                            src={backspaceKey}
                            alt=""
                            className="pin-entry-grid__backspace-img"
                        />
                    </button>
                </div>
            </div>
            <Button
                type="button"
                theme="primary"
                size="full-width"
                disabled={disabled || pin.length === 0}
                loading={loading}
                onClick={pressSubmit}
                testId="pin-submit"
            >
                {submitLabel ?? t('pinGrid.punch')}
            </Button>
        </div>
    );
}
